import { useSearchTableScopeFromRoute } from '@aibees/elephant/context/search-table-scope';
import { useSearchParamsStore } from '@aibees/elephant/store/search-params-store';
import { useMatchRoute } from '@aibees/shared/hooks';
import { useMemo } from 'react';
import { ROUTES as R } from '../router';
import {
  ElephantPresetType,
  SearchBrowseParams,
  SearchMode,
  SearchTable,
  SearchType,
} from '../types';

/**
 * Reads a single search param for the CURRENT ROUTE's scope directly from the
 * Zustand search-params store.
 *
 * Filter values (incl. `value` and `type`) live in the per-scope store, NOT the
 * URL — they're hydrated once on load then stripped (see SearchParamsUrlSync), so
 * any URL-based read is stale/false after load.
 *
 * Scope is derived from the pathname (via useSearchTableScopeFromRoute), not the
 * SearchTableScope *context*, so this works ANYWHERE — including providers mounted
 * at router root, above the scope provider (e.g. TableActionProvider). Selects a
 * primitive so subscribers only re-render when that value changes.
 */
function useScopedSearchParam(
  key: keyof SearchBrowseParams
): string | undefined {
  const scope = useSearchTableScopeFromRoute();
  return useSearchParamsStore((state) =>
    scope === 'company' || scope === 'people' || scope === 'employees'
      ? (state.paramsByScope[scope]?.[key] as string | undefined)
      : undefined
  );
}

/** URL param key for similarity/semantic search value. Use instead of hardcoding 'value'. */
export const SEARCH_PARAM_VALUE_KEY = 'value' as keyof SearchBrowseParams;

// ---------------------------------------------------------------------------
// Route-only hooks: do NOT call useSearchParams, so they won't re-render on
// filter / URL-param changes. Use these wherever you only need route-derived
// information (type, table, presetType).
// ---------------------------------------------------------------------------

function useRouteMatches() {
  const matchCompanySearch = useMatchRoute(R.searchCompany);
  const matchPeopleSearch = useMatchRoute(R.searchPeople);
  const matchCompanyEmployees = useMatchRoute(R.companyEmployees);
  return { matchCompanySearch, matchPeopleSearch, matchCompanyEmployees };
}

/** Returns only `table` (SearchTable) derived from route matching. */
export function useSearchTableFromRoute(): SearchTable | undefined {
  const { matchCompanySearch, matchPeopleSearch, matchCompanyEmployees } =
    useRouteMatches();

  return useMemo(() => {
    if (matchCompanySearch) return SearchTable.Company;
    if (matchPeopleSearch) return SearchTable.People;
    if (matchCompanyEmployees) return SearchTable.Employees;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);
}

/** Returns only `type` (SearchType) derived from route matching. */
export function useSearchTypeFromRoute(): SearchType | undefined {
  const { matchCompanySearch, matchPeopleSearch, matchCompanyEmployees } =
    useRouteMatches();

  return useMemo(() => {
    if (matchCompanySearch) return SearchType.Company;
    if (matchPeopleSearch || matchCompanyEmployees) return SearchType.People;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);
}

type SearchRouteInfo = {
  type?: SearchType;
  table?: SearchTable;
  presetType?: ElephantPresetType;
};

/** Returns `{ type, table, presetType }` — all derived from route matching. */
export function useSearchRouteInfo(): SearchRouteInfo {
  const { matchCompanySearch, matchPeopleSearch, matchCompanyEmployees } =
    useRouteMatches();

  const type = useMemo(() => {
    if (matchCompanySearch) return SearchType.Company;
    if (matchPeopleSearch || matchCompanyEmployees) return SearchType.People;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);

  const table = useMemo(() => {
    if (matchCompanySearch) return SearchTable.Company;
    if (matchPeopleSearch) return SearchTable.People;
    if (matchCompanyEmployees) return SearchTable.Employees;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch, matchCompanyEmployees]);

  const presetType = useMemo(() => {
    if (matchCompanySearch) return ElephantPresetType.COMPANY_SAVED_SEARCH;
    if (matchPeopleSearch) return ElephantPresetType.PEOPLE_SAVED_SEARCH;
    return undefined;
  }, [matchCompanySearch, matchPeopleSearch]);

  return useMemo(
    () => ({ type, table, presetType }),
    [type, table, presetType]
  );
}

/**
 * Returns `isSimilarity` (whether a similarity/semantic `value` is set).
 *
 * Store-backed via useScopedSearchParam (name kept for API stability despite
 * `FromRoute`). Works anywhere, including router-root providers.
 */
export function useSearchSimilarityFromRoute(): boolean {
  return !!useScopedSearchParam('value');
}

/** Returns `mode` (SearchMode) for the current scope (store-backed). Use with useSearchTypeFromRoute or useSearchRouteInfo when you need type + mode. */
export function useSearchModeFromRoute(): SearchMode | undefined {
  const value = useScopedSearchParam('value');
  const type = useScopedSearchParam('type');
  const isSimilarity = !!value;
  const isSemantic =
    type?.toLocaleLowerCase() === SearchMode.Semantic.toLocaleLowerCase();
  return useMemo(() => {
    if (isSemantic) return SearchMode.Semantic;
    if (isSimilarity) return SearchMode.Similarity;
    return undefined;
  }, [isSemantic, isSimilarity]);
}
